import { Head, router } from "@inertiajs/react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useConfetti } from "@/hooks/useConfetti"
import html2canvas from "html2canvas"
import jsPDF from "jspdf"
import { Download } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { NavigationHeader } from "@/components/VocationalTest/NavigationHeader"
import { calculateMicroProfile, getPrimaryArea } from "@/data/microProfiles"
import type { AdvancedResults as AdvancedResultsType, AdvancedScores } from "@/types/advancedVocational"
import type { EnneagramType, ProfileData, Stage, TestResults } from "@/types/vocational"
import { PageLayout } from "@/components/VocationalTest/PageLayout"
import iconClipboard from "@/assets/icon-clipboard-new.png"
import iconChart from "@/assets/icon-chart.png"
import iconPin from "@/assets/icon-pin.png"
import iconSteps from "@/assets/proximos-passos-seta.png"
import logo from "@/assets/logo-25segundos-new.png"
import footerImage from "@/assets/metodo-rodape.png"

const ADVANCED_ANSWERS_KEY = "advanced_answers"
const BASIC_ANSWERS_KEY = "basic_answers"

interface PdfReportData {
  nome?: string | null
  email?: string | null
  idade?: number | null
  telefone?: string | null
  escola?: string | null
  serie?: string | null
  teste_basico_inicio_at?: string | null
  teste_basico_fim_at?: string | null
  teste_avancado_inicio_at?: string | null
  teste_avancado_fim_at?: string | null
}

interface AdvancedResultsPageProps {
  stage?: Stage | null
  profiles: ProfileData[]
  pdf_report_data?: PdfReportData | null
  advanced_metrics?: Record<string, Array<{ metric: string; sub_metric: string; text: string }>> | null
  basic_feedback?: string | null
}

interface UserData extends PdfReportData {}

const careerAreas: Record<string, string[]> = {
  "Psicologia": ["Psicologia Clínica", "Psicologia Organizacional", "Psicologia Escolar", "Neuropsicologia"],
  "Fisioterapia": ["Fisioterapia Ortopédica", "Fisioterapia Neurológica", "Fisioterapia Respiratória", "Fisioterapia Esportiva"],
  "Enfermagem": ["Enfermagem Geral", "Enfermagem do Trabalho", "Enfermagem Obstétrica", "UTI/Emergência"],
  "Serviço Social": ["Assistência Social", "Políticas Públicas", "Direitos Humanos", "Gestão Social"],
  "Pedagogia": ["Educação Infantil", "Alfabetização", "Educação Especial", "Gestão Escolar"],
  "Terapia Ocupacional": ["TO Pediátrica", "TO Geriátrica", "TO em Saúde Mental", "Reabilitação"],
  "Fonoaudiologia": ["Fonoaudiologia Clínica", "Audiologia", "Disfagia", "Linguagem Infantil"],
  "Psicopedagogia": ["Psicopedagogia Clínica", "Psicopedagogia Institucional", "Diagnóstico e Intervenção"],
  "Jornalismo": ["Jornalismo Digital", "Jornalismo Investigativo", "Assessoria de Imprensa", "Edição de Conteúdo"],
  "Marketing": ["Marketing Digital", "Branding", "Performance", "Gestão de Marcas"],
  "Publicidade": ["Criação Publicitária", "Planejamento de Campanhas", "Mídia", "Redação Publicitária"],
  "Locução": ["Locução Comercial", "Rádio", "Narração", "Dublagem"],
  "Produção de Conteúdo": ["Criador de Conteúdo", "Roteirista", "Produtor de Vídeo", "Podcaster"],
  "Social Media": ["Gestão de Redes Sociais", "Estratégia Digital", "Community Manager", "Influencer Marketing"],
  "Relações Públicas": ["Comunicação Corporativa", "Eventos", "Gestão de Crise", "Relações Institucionais"],
  "Engenharia": ["Engenharia Civil", "Engenharia Mecânica", "Engenharia Elétrica", "Engenharia de Produção"],
  "Análise de Dados": ["Data Science", "Business Intelligence", "Big Data", "Analytics"],
  "TI/Programação": ["Desenvolvimento Web", "Desenvolvimento Mobile", "Backend", "Frontend", "Full Stack"],
  "Arquitetura de Software": ["DevOps", "Cloud Architecture", "Microservices", "System Design"],
  "Estatística": ["Estatística Aplicada", "Pesquisa Quantitativa", "Modelagem Estatística", "Análise Preditiva"],
  "Ciência da Computação": ["Algoritmos", "Inteligência Artificial", "Machine Learning", "Sistemas Distribuídos"],
  "Cibersegurança": ["Segurança da Informação", "Ethical Hacking", "Análise de Vulnerabilidades", "Compliance"],
  "Ciências Biológicas": ["Biologia Molecular", "Genética", "Ecologia", "Microbiologia"],
  "Biomedicina": ["Análises Clínicas", "Imagenologia", "Biologia Molecular", "Toxicologia"],
  "Pesquisa Laboratorial": ["Pesquisador", "Analista de Laboratório", "Controle de Qualidade"],
  "Química": ["Química Industrial", "Química Analítica", "Bioquímica", "Química Ambiental"],
  "Física": ["Física Teórica", "Física Experimental", "Astrofísica", "Física Médica"],
  "Matemática": ["Matemática Aplicada", "Matemática Pura", "Estatística", "Modelagem"],
  "Perícia Criminal": ["Perícia Científica", "Criminalística", "Documentoscopia", "Genética Forense"],
  "Design Gráfico": ["Design Editorial", "Design de Interface", "Motion Design", "Identidade Visual"],
  "Moda": ["Design de Moda", "Modelagem", "Estilismo", "Consultoria de Moda"],
  "Arquitetura": ["Arquitetura Residencial", "Arquitetura Comercial", "Urbanismo", "Paisagismo"],
  "Artes Visuais": ["Pintura", "Escultura", "Arte Digital", "Instalações"],
  "Cinema": ["Direção", "Produção", "Roteiro", "Edição", "Cinematografia"],
  "Fotografia": ["Fotografia Publicitária", "Fotojornalismo", "Fotografia de Moda", "Fotografia Artística"],
  "Ilustração": ["Ilustração Editorial", "Ilustração Infantil", "Concept Art", "Ilustração Digital"],
  "Game Design": ["Level Design", "Game Art", "Game Programming", "Narrative Design"],
  "Design de Produto": ["Design Industrial", "Design de Móveis", "Design de Embalagens", "Prototipagem"],
  "Engenharia de Produção": ["Gestão de Processos", "Logística", "Qualidade", "Supply Chain"],
  "UX/UI": ["UX Research", "UI Design", "Product Design", "Design System"],
  "Design 3D": ["Modelagem 3D", "Animação 3D", "Renderização", "Visualização Arquitetônica"],
  "Administração": ["Gestão Empresarial", "Gestão Financeira", "Gestão de Pessoas", "Gestão Estratégica"],
  "Gestão de Projetos": ["Scrum Master", "Product Owner", "Project Manager", "Agile Coach"],
  "Gestão de Pessoas": ["Recrutamento e Seleção", "Treinamento e Desenvolvimento", "Gestão de Talentos", "Clima Organizacional"],
  "RH": ["Business Partner", "Remuneração e Benefícios", "Desenvolvimento Organizacional", "People Analytics"],
  "Logística": ["Supply Chain", "Armazenagem", "Distribuição", "Transporte"],
  "Direito Empresarial": ["Direito Societário", "Contratos", "Compliance", "M&A"],
  "Gestão Comercial": ["Vendas", "Negociação", "Relacionamento com Cliente", "Expansão de Mercado"],
  "Startups": ["Empreendedorismo Digital", "Growth Hacking", "MVP", "Lean Startup"],
  "Negócios Digitais": ["E-commerce", "Marketplace", "SaaS", "Plataformas Digitais"],
  "Vendas": ["Inside Sales", "Field Sales", "Key Account", "Business Development"],
  "Produtos Digitais": ["Product Management", "Product Marketing", "Product Analytics", "Product Strategy"],
  "Mediação": ["Mediação de Conflitos", "Conciliação", "Arbitragem"],
  "Coaching": ["Life Coach", "Career Coach", "Executive Coach", "Team Coach"],
  "Mentoring": ["Mentoria Profissional", "Mentoria de Carreira", "Mentoria Empresarial"],
  "Comunicação": ["Comunicação Corporativa", "Comunicação Interna", "Oratória", "Comunicação Digital"],
  "Literatura": ["Escrita Criativa", "Crítica Literária", "Tradução", "Edição"],
  "Educação Física": ["Personal Trainer", "Preparação Física", "Reabilitação", "Esportes Coletivos"],
  "Ensino": ["Professor", "Coordenador Pedagógico", "Orientador Educacional", "Tutor"],
  "Orientação Educacional": ["Orientação Profissional", "Orientação Vocacional", "Aconselhamento Escolar"],
  "Economia": ["Economia Aplicada", "Macroeconomia", "Microeconomia", "Economia Comportamental"],
  "Sociologia": ["Pesquisa Social", "Políticas Públicas", "Análise Social", "Desenvolvimento Social"],
  "Administração Pública": ["Gestão Pública", "Políticas Governamentais", "Planejamento Público"],
  "Ciência Política": ["Análise Política", "Relações Internacionais", "Políticas Comparadas"],
  "Pesquisa de Opinião": ["Pesquisa Quantitativa", "Pesquisa Qualitativa", "Análise de Dados", "Insights de Consumidor"],
  "Técnico de TI": ["Suporte Técnico", "Help Desk", "Infraestrutura de TI", "Redes"],
  "Análise de Suporte": ["Analista de Suporte", "Service Desk", "Troubleshooting", "Documentação Técnica"],
  "Técnico de Eletrônica": ["Manutenção Eletrônica", "Instrumentação", "Telecomunicações"],
  "Eletrotécnica": ["Instalações Elétricas", "Manutenção Elétrica", "Projetos Elétricos"],
  "Automação": ["Automação Industrial", "Controle de Processos", "Robótica", "Sistemas Embarcados"],
}

const professionDescriptions: Record<string, Record<string, string>> = {
  "Psicologia": {
    "Psicologia Clínica": "Atendimento terapêutico individual ou em grupo, auxiliando no tratamento de questões emocionais, comportamentais e transtornos mentais.",
    "Psicologia Organizacional": "Atuação em empresas focando em seleção, treinamento, desenvolvimento de equipes e clima organizacional.",
    "Psicologia Escolar": "Trabalho em instituições educacionais, apoiando alunos, professores e famílias em questões relacionadas à aprendizagem e desenvolvimento.",
    "Neuropsicologia": "Avaliação e reabilitação de funções cognitivas afetadas por lesões cerebrais ou distúrbios neurológicos."
  },
  "Fisioterapia": {
    "Fisioterapia Ortopédica": "Tratamento de lesões musculoesqueléticas, fraturas, entorses e pós-operatórios ortopédicos.",
    "Fisioterapia Neurológica": "Reabilitação de pacientes com AVC, Parkinson, paralisia cerebral e outras condições neurológicas.",
    "Fisioterapia Respiratória": "Cuidado de pacientes com doenças pulmonares, auxílio na ventilação mecânica e reabilitação cardiorrespiratória.",
    "Fisioterapia Esportiva": "Prevenção e tratamento de lesões em atletas, além de otimização de performance."
  },
  "Enfermagem": {
    "Enfermagem Geral": "Cuidados integrais ao paciente em hospitais, clínicas e unidades básicas de saúde.",
    "Enfermagem do Trabalho": "Promoção da saúde e segurança dos trabalhadores em ambientes corporativos e industriais.",
    "Enfermagem Obstétrica": "Assistência à mulher durante gestação, parto e puerpério.",
    "UTI/Emergência": "Cuidados intensivos a pacientes críticos e atendimento de urgências médicas."
  },
  "Serviço Social": {
    "Assistência Social": "Atendimento e orientação de indivíduos e famílias em situação de vulnerabilidade social.",
    "Políticas Públicas": "Planejamento, implementação e avaliação de programas sociais governamentais.",
    "Direitos Humanos": "Defesa e promoção dos direitos fundamentais de grupos vulneráveis.",
    "Gestão Social": "Coordenação de projetos e organizações do terceiro setor."
  },
  "Pedagogia": {
    "Educação Infantil": "Ensino e cuidado de crianças de 0 a 5 anos, com foco no desenvolvimento integral.",
    "Alfabetização": "Ensino da leitura e escrita nos anos iniciais do ensino fundamental.",
    "Educação Especial": "Atendimento educacional especializado para alunos com deficiências ou necessidades específicas.",
    "Gestão Escolar": "Coordenação pedagógica e administrativa de instituições de ensino."
  },
  "Jornalismo": {
    "Jornalismo Digital": "Produção de conteúdo para portais, blogs e redes sociais com foco em velocidade e interatividade.",
    "Jornalismo Investigativo": "Apuração profunda de fatos relevantes ao interesse público, denúncias e reportagens especiais.",
    "Assessoria de Imprensa": "Gestão da comunicação de empresas, órgãos públicos e personalidades com a mídia.",
    "Edição de Conteúdo": "Revisão, curadoria e direção editorial de materiais jornalísticos."
  },
  "Marketing": {
    "Marketing Digital": "Estratégias online incluindo SEO, anúncios pagos, e-mail marketing e automação.",
    "Branding": "Construção e gestão de identidade e posicionamento de marcas.",
    "Performance": "Otimização de campanhas com foco em métricas, conversões e ROI.",
    "Gestão de Marcas": "Planejamento estratégico de longo prazo para crescimento e relevância de marcas."
  },
  "Engenharia": {
    "Engenharia Civil": "Projeto, construção e manutenção de obras de infraestrutura como edifícios, pontes e estradas.",
    "Engenharia Mecânica": "Desenvolvimento de sistemas mecânicos, máquinas e processos industriais.",
    "Engenharia Elétrica": "Projeto e manutenção de sistemas elétricos, eletrônicos e de potência.",
    "Engenharia de Produção": "Otimização de processos produtivos, logística e gestão industrial."
  },
  "Análise de Dados": {
    "Data Science": "Extração de insights de grandes volumes de dados usando estatística, machine learning e programação.",
    "Business Intelligence": "Análise de dados empresariais para suporte à tomada de decisão estratégica.",
    "Big Data": "Processamento e análise de conjuntos massivos de dados estruturados e não estruturados.",
    "Analytics": "Interpretação de métricas e KPIs para otimização de processos e estratégias."
  },
  "TI/Programação": {
    "Desenvolvimento Web": "Criação de sites e aplicações web responsivas e funcionais.",
    "Desenvolvimento Mobile": "Desenvolvimento de aplicativos para iOS e Android.",
    "Backend": "Programação de servidores, bancos de dados e APIs.",
    "Frontend": "Desenvolvimento de interfaces de usuário e experiência visual.",
    "Full Stack": "Domínio completo de desenvolvimento frontend e backend."
  },
  "Design Gráfico": {
    "Design Editorial": "Criação de layouts para livros, revistas e materiais impressos.",
    "Design de Interface": "Projeto visual de interfaces digitais (apps, sites, sistemas).",
    "Motion Design": "Animação de elementos gráficos para vídeos e mídias digitais.",
    "Identidade Visual": "Desenvolvimento de logos, marcas e sistemas visuais para empresas."
  },
  "Arquitetura": {
    "Arquitetura Residencial": "Projeto de casas e apartamentos com foco em funcionalidade e estética.",
    "Arquitetura Comercial": "Design de espaços corporativos, lojas e estabelecimentos comerciais.",
    "Urbanismo": "Planejamento de cidades, bairros e espaços públicos.",
    "Paisagismo": "Projeto de jardins, parques e áreas verdes."
  },
  "Administração": {
    "Gestão Empresarial": "Planejamento estratégico e operacional de empresas.",
    "Gestão Financeira": "Controle de finanças, investimentos e orçamento corporativo.",
    "Gestão de Pessoas": "Administração de recursos humanos e desenvolvimento de talentos.",
    "Gestão Estratégica": "Definição de metas, visão e direcionamento de longo prazo."
  },
  "Direito Empresarial": {
    "Direito Societário": "Atuação em constituição, reestruturação e governança de empresas.",
    "Contratos": "Elaboração e análise de contratos comerciais e corporativos.",
    "Compliance": "Garantia de conformidade legal e ética nas operações empresariais.",
    "M&A": "Assessoria em fusões, aquisições e operações societárias."
  }
}

Object.keys(careerAreas).forEach((career) => {
  if (!professionDescriptions[career]) {
    professionDescriptions[career] = {}
    careerAreas[career].forEach((subCareer) => {
      professionDescriptions[career][subCareer] = `Especialização em ${subCareer}, oferecendo oportunidades de desenvolvimento profissional nesta área.`
    })
  }
})

function computeBasicResults(profiles: ProfileData[]): TestResults | null {
  const raw = window.localStorage.getItem(BASIC_ANSWERS_KEY)
  if (!raw) return null

  const lookup: Record<number, ProfileData> = {}
  profiles.forEach((p) => { lookup[p.tipo] = p })

  const counts: Record<EnneagramType, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0 }
  const answers: Record<number, EnneagramType> = JSON.parse(raw)
  Object.values(answers).forEach((value) => {
    counts[value] += 1
  })

  const total = Object.values(counts).reduce((sum, value) => sum + value, 0)
  const sorted = (Object.entries(counts) as [string, number][])
    .map(([type, count]) => [Number(type) as EnneagramType, count] as [EnneagramType, number])
    .sort((a, b) => b[1] - a[1])
  const dominant = sorted[0]
  if (!dominant || dominant[1] === 0) return null
  const profile = lookup[dominant[0]]
  if (!profile) return null

  const profissoes = Array.isArray(profile.profissoes_indicadas)
    ? profile.profissoes_indicadas
    : (profile.profissoes_indicadas || "").split("\n").filter(Boolean)

  return {
    dominantType: {
      type: dominant[0],
      name: profile.titulo || `Tipo ${dominant[0]}`,
      count: dominant[1],
      percentage: total > 0 ? Math.round((dominant[1] / total) * 100) : 0,
      description: profile.description || "",
      talent: profile.talent || "",
      style: profile.style || "",
      careers: profissoes,
      vocationalArea: profile.vocationalArea || "",
    },
    secondaryType: undefined,
    allTypes: (Object.entries(counts) as [string, number][]).map(([type, count]) => ({
      type: Number(type) as EnneagramType,
      name: lookup[Number(type)]?.titulo || `Tipo ${type}`,
      count,
      percentage: total > 0 ? Math.round((count / total) * 100) : 0,
    }))
  }
}

function computeAdvancedResults(
  stage: Stage,
  answers: Record<number, number>,
  metricsMap: Record<string, Array<{ metric: string; sub_metric: string; text: string }>> | null | undefined
): AdvancedResultsType | null {
  const scores: AdvancedScores = {
    H: 0,
    E: 0,
    B: 0,
    A: 0,
    G: 0,
    C: 0,
  }

  Object.entries(answers).forEach(([questionIdStr, optionValue]) => {
    const questionId = Number(questionIdStr)
    const question = stage.questions.find((q) => q.id === questionId)
    if (!question || !question.options || !metricsMap) return
    const metrics = metricsMap[String(questionId)]
    if (!metrics || metrics.length === 0) return
    const selectedOption = question.options.find((option) => option.value === optionValue)
    if (!selectedOption) return
    const metricOption = metrics.find((option) => option.text.trim() === selectedOption.text.trim())
    if (!metricOption) return
    scores[metricOption.metric as keyof AdvancedScores] += 1
  })

  const microProfile = calculateMicroProfile(scores)
  const primaryArea = getPrimaryArea(scores)

  let behavioralTendency = ""
  if (scores.B > scores.E && scores.B > scores.A) {
    behavioralTendency = "Cuidador e empático"
  } else if (scores.E > scores.H && scores.E > scores.B) {
    behavioralTendency = "Analítico e lógico"
  } else if (scores.A > scores.E && scores.A > scores.B) {
    behavioralTendency = "Criativo e expressivo"
  } else if (scores.G > scores.A && scores.G > scores.B) {
    behavioralTendency = "Organizador e estratégico"
  } else if (scores.H > scores.E) {
    behavioralTendency = "Comunicativo e inspirador"
  } else {
    behavioralTendency = "Multifacetado e adaptável"
  }

  return {
    microProfile,
    scores,
    primaryArea,
    behavioralTendency
  }
}

export default function AdvancedResultsPage({ stage, profiles, pdf_report_data, advanced_metrics, basic_feedback }: AdvancedResultsPageProps) {
  const resultsRef = useRef<HTMLDivElement>(null)
  const [userData, setUserData] = useState<UserData | null>(null)
  useConfetti()

  const advancedAnswers = useMemo(() => {
    const raw = window.localStorage.getItem(ADVANCED_ANSWERS_KEY)
    return raw ? (JSON.parse(raw) as Record<number, number>) : {}
  }, [])

  const basicResults = useMemo(() => computeBasicResults(profiles), [profiles])
  const advancedResults = useMemo(() => {
    if (!stage) return null
    return computeAdvancedResults(stage, advancedAnswers, advanced_metrics)
  }, [stage, advancedAnswers, advanced_metrics])

  const profileData = useMemo(() => {
    if (!basicResults) return null
    return profiles.find((p) => p.tipo === basicResults.dominantType.type) || null
  }, [basicResults, profiles])

  useEffect(() => {
    if (pdf_report_data) {
      setUserData(pdf_report_data)
    }
  }, [pdf_report_data])

  if (!stage || !advancedResults) {
    return (
      <>
        <Head title="Resultados avançados" />
        <PageLayout>
          <div className="w-full max-w-2xl mx-auto">
            <Card className="p-8 md:p-10 text-center shadow-elegant">
              <h1 className="text-2xl md:text-3xl font-bold text-title-blue">Resultados Avançados</h1>
              <p className="text-muted-foreground mt-4">
                Nenhuma resposta avançada encontrada.
              </p>
              <div className="mt-6 flex flex-col gap-3">
                <Button onClick={() => router.visit("/advanced-questions")} className="bg-gradient-primary hover:opacity-90 transition-opacity">
                  Fazer teste avançado
                </Button>
                <Button onClick={() => router.visit("/results")} variant="outline">
                  Voltar ao resultado básico
                </Button>
              </div>
            </Card>
          </div>
        </PageLayout>
      </>
    )
  }

  const { microProfile, scores, primaryArea, behavioralTendency } = advancedResults

  const handleDownloadPDF = async () => {
    if (!resultsRef.current) return

    try {
      const pdf = new jsPDF("p", "mm", "a4")
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const pageMargin = 15
      const maxWidth = pageWidth - (2 * pageMargin)
      let yPosition = pageMargin

      const logoImg = new Image()
      logoImg.src = logo
      await new Promise((resolve) => {
        logoImg.onload = resolve
      })

      pdf.setTextColor(16, 42, 88)

      const logoWidth = 80
      const logoHeight = (logoImg.height * logoWidth) / logoImg.width
      pdf.addImage(logoImg, "PNG", (pageWidth - logoWidth) / 2, yPosition, logoWidth, logoHeight)
      yPosition += logoHeight + 15

      if (userData) {
        pdf.setFontSize(12)
        pdf.setFont("helvetica", "bold")
        pdf.text("Dados do Participante", pageMargin, yPosition)
        yPosition += 8

        pdf.setFontSize(10)
        pdf.setFont("helvetica", "normal")
        if (userData.nome) {
          pdf.text(`Nome: ${userData.nome}`, pageMargin + 5, yPosition)
          yPosition += 6
        }
        if (userData.email) {
          pdf.text(`Email: ${userData.email}`, pageMargin + 5, yPosition)
          yPosition += 6
        }
        if (userData.idade) {
          pdf.text(`Idade: ${userData.idade} anos`, pageMargin + 5, yPosition)
          yPosition += 6
        }
        if (userData.telefone) {
          pdf.text(`Telefone: ${userData.telefone}`, pageMargin + 5, yPosition)
          yPosition += 6
        }
        if (userData.escola && userData.escola !== "Externo") {
          pdf.text(`Escola: ${userData.escola}`, pageMargin + 5, yPosition)
          yPosition += 6
        }
        if (userData.serie && userData.serie.trim() !== "") {
          pdf.text(`Série: ${userData.serie}`, pageMargin + 5, yPosition)
          yPosition += 6
        }

        if (userData.teste_basico_inicio_at && userData.teste_basico_fim_at) {
          const startTime = new Date(userData.teste_basico_inicio_at)
          const endTime = new Date(userData.teste_basico_fim_at)
          const durationMinutes = Math.round((endTime.getTime() - startTime.getTime()) / 1000 / 60)
          pdf.text(`Tempo teste básico: ${durationMinutes} minutos`, pageMargin + 5, yPosition)
          yPosition += 6
        }

        if (userData.teste_avancado_inicio_at && userData.teste_avancado_fim_at) {
          const startTime = new Date(userData.teste_avancado_inicio_at)
          const endTime = new Date(userData.teste_avancado_fim_at)
          const durationMinutes = Math.round((endTime.getTime() - startTime.getTime()) / 1000 / 60)
          pdf.text(`Tempo teste avançado: ${durationMinutes} minutos`, pageMargin + 5, yPosition)
          yPosition += 6
        }

        yPosition += 15
      }

      const sections = resultsRef.current.querySelectorAll(".pdf-section")
      for (let i = 0; i < sections.length; i += 1) {
        const section = sections[i] as HTMLElement
        if (i > 0) {
          pdf.addPage()
          yPosition = pageMargin
          pdf.addImage(logoImg, "PNG", (pageWidth - logoWidth) / 2, yPosition, logoWidth, logoHeight)
          yPosition += logoHeight + 15
        }

        const canvas = await html2canvas(section, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
        })

        const imgData = canvas.toDataURL("image/png")
        const imgWidth = maxWidth
        const imgHeight = (canvas.height * imgWidth) / canvas.width
        if (yPosition + imgHeight > pageHeight - 40) {
          pdf.addPage()
          yPosition = pageMargin
          pdf.addImage(logoImg, "PNG", (pageWidth - logoWidth) / 2, yPosition, logoWidth, logoHeight)
          yPosition += logoHeight + 15
        }

        pdf.addImage(imgData, "PNG", pageMargin, yPosition, imgWidth, imgHeight)
        yPosition += imgHeight + 10
      }

      pdf.addPage()
      yPosition = pageMargin
      pdf.addImage(logoImg, "PNG", (pageWidth - logoWidth) / 2, yPosition, logoWidth, logoHeight)
      yPosition += logoHeight + 15

      pdf.setFontSize(18)
      pdf.setFont("helvetica", "bold")
      pdf.setTextColor(16, 42, 88)
      pdf.text("Especializações Detalhadas", pageWidth / 2, yPosition, { align: "center" })
      yPosition += 12

      pdf.setFontSize(10)
      pdf.setFont("helvetica", "normal")
      const intro = "Cada área apresenta múltiplas possibilidades de especialização. Conheça em detalhes:"
      const introLines = pdf.splitTextToSize(intro, maxWidth)
      pdf.text(introLines, pageMargin, yPosition)
      yPosition += introLines.length * 6 + 10

      microProfile.careers.forEach((career) => {
        const subCareers = careerAreas[career]
        const descriptions = professionDescriptions[career]

        if (subCareers && descriptions) {
          const estimatedHeight = 20 + subCareers.length * 18
          if (yPosition + estimatedHeight > pageHeight - 40) {
            pdf.addPage()
            yPosition = pageMargin
            pdf.addImage(logoImg, "PNG", (pageWidth - logoWidth) / 2, yPosition, logoWidth, logoHeight)
            yPosition += logoHeight + 15
          }

          pdf.setFontSize(13)
          pdf.setFont("helvetica", "bold")
          pdf.setTextColor(16, 42, 88)
          pdf.text(`${career}`, pageMargin, yPosition)
          yPosition += 8

          pdf.setFontSize(9)
          pdf.setFont("helvetica", "normal")
          subCareers.forEach((subCareer) => {
            const description = descriptions[subCareer] || ""
            if (yPosition > pageHeight - 45) {
              pdf.addPage()
              yPosition = pageMargin
              pdf.addImage(logoImg, "PNG", (pageWidth - logoWidth) / 2, yPosition, logoWidth, logoHeight)
              yPosition += logoHeight + 15
            }

            pdf.setFont("helvetica", "bold")
            const titleLines = pdf.splitTextToSize(`• ${subCareer}`, maxWidth - 5)
            pdf.text(titleLines, pageMargin + 3, yPosition)
            yPosition += titleLines.length * 4 + 2

            pdf.setFont("helvetica", "normal")
            pdf.setTextColor(80, 80, 80)
            const descLines = pdf.splitTextToSize(description, maxWidth - 8)
            pdf.text(descLines, pageMargin + 6, yPosition)
            yPosition += descLines.length * 4 + 5
            pdf.setTextColor(16, 42, 88)
          })

          yPosition += 6
        }
      })

      const footerImg = new Image()
      footerImg.src = footerImage
      await new Promise((resolve) => {
        footerImg.onload = resolve
      })
      const totalPages = pdf.getNumberOfPages()
      for (let i = 1; i <= totalPages; i += 1) {
        pdf.setPage(i)
        const footerWidth = pageWidth - 40
        const footerHeight = (footerImg.height * footerWidth) / footerImg.width
        const footerY = pageHeight - footerHeight - 10
        pdf.addImage(footerImg, "PNG", 20, footerY, footerWidth, footerHeight)
      }

      pdf.save("resultado-avancado-semear.pdf")
    } catch (error) {
      console.error("Erro ao gerar PDF:", error)
    }
  }

  return (
    <>
      <Head title="Resultados avançados" />
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/10 pb-16">
        <NavigationHeader />

        <div className="w-full max-w-5xl mx-auto px-4 py-12 space-y-8">
          <div ref={resultsRef} className="space-y-10 bg-background p-8 rounded-2xl shadow-xl">
            <div className="pdf-section text-center space-y-6 page-break-inside-avoid pb-6">
              <div className="flex items-center justify-center gap-3 mb-4">
                <img src={iconClipboard} alt="Resultado" className="w-16 h-16" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold leading-tight" style={{ color: "#102a58" }}>
                Seu Resultado Avançado
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground font-medium">
                Perfil Profissional SEMEAR
              </p>
            </div>

            <Card className="pdf-section p-8 md:p-10 space-y-6 page-break-inside-avoid bg-gradient-to-br from-primary/5 to-background border-2">
              <p className="text-lg md:text-xl leading-relaxed font-medium">
                🎉 Parabéns! Você concluiu a segunda etapa do teste e agora temos um retrato muito mais completo de quem você é — não apenas seu modo de pensar, mas seu modo de{" "}
                <span className="font-bold" style={{ color: "#102a58" }}>sentir, decidir, aprender, trabalhar e crescer</span>.
              </p>
              <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
                Este resultado avançado integra três dimensões fundamentais: <span className="font-semibold text-foreground">sua forma de processar o mundo (cognitivo)</span>, <span className="font-semibold text-foreground">sua forma de se relacionar com emoções (emocional)</span> e <span className="font-semibold text-foreground">suas afinidades naturais com áreas de conhecimento (vocacional)</span>.
              </p>
              <div className="p-4 bg-background/80 rounded-lg border-l-4" style={{ borderLeftColor: "#102a58" }}>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  ✧ O Método <span className="font-bold text-foreground">S.E.M.E.A.R.</span> (Sentir, Entender, Mudar, Escolher, Agir e Realizar) é a base deste mapeamento profundo da sua identidade profissional.
                </p>
              </div>
            </Card>

            {basicResults && profileData && (
              <Card className="pdf-section p-6 md:p-8 space-y-4 bg-gradient-to-br from-secondary/5 to-secondary/10 page-break-inside-avoid">
                <h2 className="text-2xl font-bold" style={{ color: "#102a58" }}>
                  Seu Perfil Eneagrama - TIPO {basicResults.dominantType.type}
                </h2>
                <h3 className="text-xl font-semibold text-primary">
                  {profileData.titulo}
                </h3>

                <div className="space-y-3">
                  <p className="text-base leading-relaxed">
                    {profileData.descricao_curta}
                  </p>

                  <div>
                    <p className="font-semibold text-foreground mb-2">✺ Seu Talento Natural:</p>
                    <p className="text-muted-foreground">{basicResults.dominantType.talent}</p>
                  </div>

                  <div>
                    <p className="font-semibold text-foreground mb-2">✺ Seu Estilo de Ação:</p>
                    <p className="text-muted-foreground">{basicResults.dominantType.style}</p>
                  </div>

                  {basic_feedback && (
                    <div className="mt-4 p-4 bg-background/50 rounded-lg">
                      <p className="font-semibold text-foreground mb-3">✧ Orientação Personalizada:</p>
                      <div className="text-sm text-muted-foreground leading-relaxed space-y-1">
                        {basic_feedback.split("\n").map((line, i) => {
                          const trimmed = line.trim()
                          if (!trimmed) return null
                          if (trimmed.startsWith("•")) {
                            const parts = trimmed.slice(1).trim().split(/\*\*(.+?)\*\*/)
                            return (
                              <div key={i} className="flex gap-2 items-start pl-1">
                                <span className="text-primary mt-0.5">•</span>
                                <span>
                                  {parts.map((part, j) =>
                                    j % 2 === 1
                                      ? <strong key={j} className="text-foreground">{part}</strong>
                                      : <span key={j}>{part}</span>
                                  )}
                                </span>
                              </div>
                            )
                          }
                          return <p key={i}>{trimmed}</p>
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            )}

            <Card className="pdf-section p-6 md:p-8 space-y-6 bg-gradient-to-br from-primary/5 to-primary/10 page-break-inside-avoid">
              <h2 className="text-2xl font-bold" style={{ color: "#102a58" }}>
                {microProfile.name}
              </h2>

              <div className="space-y-4">
                <div>
                  <span className="font-semibold text-foreground">Área Vocacional Principal:</span>
                  <span className="ml-2 text-muted-foreground">{primaryArea}</span>
                </div>

                <div>
                  <span className="font-semibold text-foreground">Tendência Comportamental:</span>
                  <span className="ml-2 text-muted-foreground">{behavioralTendency}</span>
                </div>

                <div>
                  <span className="font-semibold text-foreground">Direção Profissional:</span>
                  <span className="ml-2 text-muted-foreground">{microProfile.name}</span>
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-base leading-relaxed">
                  <span className="font-semibold text-primary">Descrição do seu perfil:</span> {microProfile.description}
                </p>

                <p className="text-base leading-relaxed">
                  Isso mostra que você se desenvolve melhor quando pode{" "}
                  <span className="font-semibold text-primary">{microProfile.characteristics}</span>, e tende a se destacar em ambientes que envolvem{" "}
                  <span className="font-semibold text-primary">{microProfile.environment}</span>.
                </p>

                <p className="text-base leading-relaxed text-muted-foreground">
                  <span className="font-semibold text-foreground">O que isso significa na prática:</span> Seu perfil indica que você tem uma combinação única de habilidades e preferências. Quando você escolhe caminhos profissionais alinhados com essas características naturais, o trabalho deixa de ser apenas uma obrigação e se torna uma expressão de quem você realmente é.
                </p>
              </div>
            </Card>

            <Card className="pdf-section p-6 md:p-8 space-y-4 page-break-before">
              <div className="flex items-center gap-3 mb-4">
                <img src={iconChart} alt="Gráfico" className="w-12 h-12" />
                <h3 className="text-xl font-bold text-foreground">
                  Suas Pontuações por Área
                </h3>
              </div>

              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                Este mapeamento mostra suas afinidades naturais com diferentes áreas do conhecimento. Quanto maior a pontuação, mais forte é sua conexão com aquele campo. Lembre-se: você não precisa escolher apenas a área com maior pontuação — às vezes, as combinações entre áreas revelam caminhos ainda mais interessantes.
              </p>

              <div className="space-y-3">
                {Object.entries(scores).filter(([key]) => key !== "C").map(([area, score]) => {
                  const areaNames: Record<string, string> = {
                    H: "Humanas",
                    E: "Exatas",
                    B: "Biológicas",
                    A: "Artes",
                    G: "Gestão"
                  }

                  const areaDescriptions: Record<string, string> = {
                    H: "Comunicação, relacionamento humano, ensino, mediação",
                    E: "Lógica, análise, tecnologia, resolução de problemas",
                    B: "Cuidado, saúde, bem-estar, ciências da vida",
                    A: "Criatividade, expressão, design, inovação estética",
                    G: "Organização, liderança, processos, empreendedorismo"
                  }

                  const maxScore = Math.max(...Object.entries(scores).filter(([k]) => k !== "C").map(([, v]) => v))
                  const percentage = maxScore > 0 ? (score / maxScore) * 100 : 0

                  return (
                    <div key={area} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-foreground">
                          {areaNames[area] || area}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {score} pontos
                        </span>
                      </div>
                      <div className="w-full h-2 rounded-full bg-muted">
                        <div
                          className="h-2 rounded-full bg-primary transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {areaDescriptions[area] || ""}
                      </p>
                    </div>
                  )
                })}
              </div>

              <p className="text-xs text-muted-foreground leading-relaxed mt-4 italic">
                * Sua pontuação reflete não apenas preferências intelectuais, mas também seu estilo emocional, comportamental e cognitivo ao lidar com cada área.
              </p>
            </Card>

            <Card className="pdf-section p-6 md:p-8 space-y-6 page-break-before">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <img src={iconPin} alt="Caminho" className="w-12 h-12" />
                  <h3 className="text-2xl font-bold text-foreground">
                    Caminhos que combinam com você
                  </h3>
                </div>
                <p className="text-muted-foreground">
                  Com base na combinação do seu perfil emocional + cognitivo + vocacional,
                  as profissões que têm maior aderência ao seu jeito de funcionar são:
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {microProfile.careers.map((career, index) => {
                  const hasSubCareers = careerAreas[career]

                  if (hasSubCareers) {
                    return (
                      <Dialog key={index}>
                        <DialogTrigger asChild>
                          <div
                            className="p-4 rounded-lg border border-border cursor-pointer transition-all hover:shadow-lg"
                            style={{ backgroundColor: "#102a58", color: "white" }}
                          >
                            <span className="font-medium">{career}</span>
                            <span className="ml-2 text-xs opacity-75">▼</span>
                          </div>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-background z-50">
                          <DialogHeader>
                            <DialogTitle className="text-2xl" style={{ color: "#102a58" }}>{career}</DialogTitle>
                            <DialogDescription className="text-base">
                              Áreas de atuação e especializações disponíveis
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 mt-6">
                            {hasSubCareers.map((subCareer, subIndex) => {
                              const description = professionDescriptions[career]?.[subCareer] || "Especialização profissional nesta área."
                              return (
                                <div
                                  key={subIndex}
                                  className="p-4 rounded-lg bg-gradient-to-br from-secondary/20 to-secondary/5 border-l-4"
                                  style={{ borderLeftColor: "#102a58" }}
                                >
                                  <h4 className="font-semibold text-foreground mb-2 text-base">
                                    {subCareer}
                                  </h4>
                                  <p className="text-sm text-muted-foreground leading-relaxed">
                                    {description}
                                  </p>
                                </div>
                              )
                            })}
                          </div>
                        </DialogContent>
                      </Dialog>
                    )
                  }

                  return (
                    <div
                      key={index}
                      className="p-4 rounded-lg border border-border"
                      style={{ backgroundColor: "#102a58", color: "white" }}
                    >
                      <span className="font-medium">{career}</span>
                    </div>
                  )
                })}
              </div>

              <div className="space-y-3 mt-6">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <span className="font-semibold text-foreground">Por que essas profissões?</span> Elas não foram escolhidas apenas pela área de conhecimento, mas porque se alinham com sua forma natural de aprender, de se relacionar, de resolver problemas e de buscar sentido no que faz. São caminhos onde você pode ser autêntico e eficaz ao mesmo tempo.
                </p>
              </div>

              <div className="space-y-3 mt-6">
                <p className="text-sm font-semibold text-foreground">
                  Essas opções aparecem não apenas pela área, mas também pelo seu modo natural de:
                </p>

                <ul className="text-sm text-muted-foreground space-y-1.5 pl-5">
                  <li>• <span className="font-medium">Tomar decisões</span> - Como você processa informações e define prioridades</li>
                  <li>• <span className="font-medium">Aprender</span> - Seu estilo de absorção e organização de conhecimento</li>
                  <li>• <span className="font-medium">Lidar com pessoas</span> - Sua forma de se comunicar e se relacionar</li>
                  <li>• <span className="font-medium">Lidar com problemas</span> - Como você reage a desafios e adversidades</li>
                  <li>• <span className="font-medium">Agir sob pressão</span> - Sua resposta em momentos críticos</li>
                  <li>• <span className="font-medium">Criar e analisar</span> - Seu equilíbrio entre intuição e lógica</li>
                  <li>• <span className="font-medium">Buscar sentido</span> - O que te motiva e te mantém engajado</li>
                </ul>
              </div>
            </Card>

            <Card className="pdf-section p-6 md:p-8 space-y-4 bg-gradient-to-br from-accent/5 to-accent/10 page-break-inside-avoid">
              <div className="flex items-center gap-3">
                <img src={iconSteps} alt="Próximos Passos" className="w-20 h-20 object-contain" />
                <h3 className="text-xl font-bold text-foreground">
                  Próximos Passos
                </h3>
              </div>

              <p className="text-base leading-relaxed">
                Use essas possibilidades como ponto de partida para sua jornada profissional.
                Este resultado é uma bússola, não um destino final.
              </p>

              <p className="text-sm text-muted-foreground leading-relaxed">
                <span className="font-semibold text-foreground">Aprofunde seu autoconhecimento:</span> Nos blocos avançados (livro e curso) você poderá explorar com ainda mais profundidade:
              </p>

              <ul className="text-sm text-muted-foreground space-y-1.5 pl-5">
                <li>• <span className="font-medium">Sua motivação profunda</span> - O que realmente te move</li>
                <li>• <span className="font-medium">Seus bloqueios</span> - Padrões que te impedem de avançar</li>
                <li>• <span className="font-medium">Seu estilo de aprendizagem</span> - Como você aprende melhor</li>
                <li>• <span className="font-medium">Sua rotina ideal</span> - Ambientes e ritmos que potencializam você</li>
                <li>• <span className="font-medium">Seus caminhos de crescimento</span> - Próximos passos concretos</li>
                <li>• <span className="font-medium">Seu mapa de vida no SEMEAR</span> - Integração completa do método</li>
              </ul>

              <p className="text-xs text-muted-foreground leading-relaxed mt-4 italic">
                ✧ Lembre-se: conhecer-se é um processo contínuo. Este resultado é apenas o começo de uma jornada de transformação.
              </p>
            </Card>
          </div>

          <div className="flex flex-col gap-4 items-center pt-4">
            <Button
              onClick={handleDownloadPDF}
              size="lg"
              className="w-full sm:w-auto bg-gradient-primary hover:opacity-90 transition-all text-white"
            >
              <Download className="w-5 h-5 mr-2" />
              Baixar Resultados Completos (PDF)
            </Button>

            <div className="flex flex-col sm:flex-row gap-3 w-full max-w-lg">
              <Button
                size="lg"
                className="flex-1 bg-[#ED7723] hover:bg-[#ED7723]/90 text-white font-bold"
                onClick={() => router.visit("/checkout")}
              >
                OFERTA ESPECIAL
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="flex-1 font-semibold"
                onClick={() => router.visit("/")}
              >
                Nesse momento me contento com esse resultado
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
